/**
 * 视图定向预览渲染器。
 *
 * 职责：
 * 1. 在 Viewer 容器右上角创建并管理预览浮层（div + canvas）；
 * 2. 订阅 Viewer 的视图定向 hover 事件；
 * 3. 使用独立 WebGPURenderer 渲染标准视图预览；
 * 4. 管理渲染并发，仅保留最新一次 hover 请求；
 * 5. 统一释放 DOM / 订阅 / GPU 资源。
 */

import {EMPTY} from 'rxjs';
import type {Subscription} from 'rxjs';
import * as THREE from 'three/webgpu';
import {CadThemeViewportBackgroundColor, ThemeEventName,} from '../../eventBus';
import type {ThemeChangedEventPayload} from '../../eventBus';
import {useEvent} from '../../hooks/useEvent';
import type {Viewer} from '../../viewer/Viewer';
import type {StandardViewCameraFrame, StandardViewType} from './standardViewTypes';
import {ViewOrientationMesh} from './ViewOrientationMesh';

/**
 * 视图定向预览渲染器。
 */
export class ViewOrientationPreviewRenderer {
    /** 预览透视相机 FOV（度），与 previewCamera 保持一致。 */
    public static readonly PREVIEW_PERSPECTIVE_FOV = 45;

    /**
     * 视图定向预览相机调试名称。
     *
     * 设计说明：
     * 1. 用于区分右上角预览画布使用的独立相机与主视口相机；
     * 2. 名称只服务调试排查，不参与预览渲染或 layer 过滤逻辑。
     */
    private static readonly PREVIEW_CAMERA_DEBUG_NAME = 'PuppyCAD视图定向预览相机';


    /** 预览相机允许的最小近裁剪面。 */
    private static readonly PREVIEW_CAMERA_MIN_NEAR = 0.000001;

    /** 预览相机近裁剪面相对观察距离的比例。 */
    private static readonly PREVIEW_CAMERA_NEAR_DISTANCE_RATIO = 0.01;

    /** 预览相机默认近裁剪面上限。 */
    private static readonly PREVIEW_CAMERA_DEFAULT_NEAR = 0.1;

    /** 预览相机远裁剪面相对观察距离的比例。 */
    private static readonly PREVIEW_CAMERA_FAR_DISTANCE_RATIO = 100;

    /** 预览相机允许的最小远裁剪面。 */
    private static readonly PREVIEW_CAMERA_MIN_FAR = 1;


    /** 预览浮层容器。 */
    private overlayElement: HTMLDivElement | null = null;

    /** 预览画布。 */
    private canvasElement: HTMLCanvasElement | null = null;

    /** 当前预览渲染器实例（懒加载，仅在首次渲染时初始化）。 */
    private renderer: THREE.WebGPURenderer | null = null;

    /** 当前绑定的画布元素，用于判断是否需要重建渲染器。 */
    private currentCanvas: HTMLCanvasElement | null = null;

    /** 预览 hover 事件订阅。 */
    private hoverSubscription: Subscription | null = null;


    /** 主题变化事件订阅。 */
    private themeSubscription: Subscription | null = null;

    /** 事件总线操作集合。 */
    private readonly eventBus = useEvent();

    /** 当前视图定向预览背景色。 */
    private previewBackgroundColor: CadThemeViewportBackgroundColor = CadThemeViewportBackgroundColor.Light;


    /** 当前绑定的 Viewer。 */
    private boundViewer: Viewer | null = null;

    /** 是否正在执行渲染。 */
    private isRendering: boolean = false;

    /** 待消费的最新 hover 视图。 */
    private pendingViewType: StandardViewType | null = null;

    /**
     * 预览用透视相机。
     * 宽高比固定为 1:1（正方形预览窗口）。
     */
    private readonly previewCamera: THREE.PerspectiveCamera = new THREE.PerspectiveCamera(
        ViewOrientationPreviewRenderer.PREVIEW_PERSPECTIVE_FOV,
        1,
        0.1,
        100000,
    );

    /**
     * 构造视图定向预览渲染器。
     */
    public constructor() {
        // 预览相机独立于主视口相机，中文名称能帮助排查 hover 预览是否误影响主相机。
        this.previewCamera.name = ViewOrientationPreviewRenderer.PREVIEW_CAMERA_DEBUG_NAME;
    }

    /** 渲染器初始化 Promise 引用，防止并发初始化。 */
    private initPromise: Promise<void> | null = null;

    /** 预览窗口边长（像素）。 */
    private static readonly PREVIEW_SIZE = 320;

    /**
     * 启动视图定向预览。
     *
     * @param viewer Viewer 实例。
     * @param container Viewer 视口容器。
     */
    public start(viewer: Viewer, container: HTMLElement): void {
        this.stop();
        this.boundViewer = viewer;
        this.ensureOverlayMounted(container);

        this.syncInitialThemeBackground(container);
        this.subscribeThemeChange();

        this.setOverlayVisible(false);


        // 配置预览相机只显示 layer 0-30，排除视角定位 mesh 所在的 layer 31
        this.previewCamera.layers.set(0);
        for (let i = 1; i < ViewOrientationMesh.MESH_LAYER; i++) {
            this.previewCamera.layers.enable(i);
        }


        const hoverStream = viewer.cameraManager?.observeViewOrientationHover() ?? EMPTY;
        this.hoverSubscription = hoverStream.subscribe((viewType) => {
            if (viewType === null) {
                this.pendingViewType = null;
                this.setOverlayVisible(false);
                return;
            }
            this.setOverlayVisible(true);
            this.enqueueRender(viewType);
        });
    }

    /**
     * 停止视图定向预览。
     * 仅释放运行态资源，不销毁 DOM/GPU（便于后续快速重启）。
     */
    public stop(): void {
        this.hoverSubscription?.unsubscribe();
        this.hoverSubscription = null;

        this.themeSubscription?.unsubscribe();
        this.themeSubscription = null;

        this.boundViewer = null;
        this.pendingViewType = null;
        this.isRendering = false;
        this.setOverlayVisible(false);
    }

    /**
     * 销毁预览渲染器，释放 DOM、订阅与 GPU 资源。
     */
    public dispose(): void {
        this.stop();
        this.disposeRenderer();
        if (this.overlayElement?.parentElement) {
            this.overlayElement.parentElement.removeChild(this.overlayElement);
        }
        this.overlayElement = null;
        this.canvasElement = null;
    }

    /**
     * 入队一次预览渲染，并触发渲染循环。
     *
     * @param viewType 目标标准视图类型。
     */
    private enqueueRender(viewType: StandardViewType): void {
        this.pendingViewType = viewType;
        if (this.isRendering) {
            return;
        }
        void this.flushRenderQueueAsync();
    }

    /**
     * 消费渲染队列（仅保留最新一次）。
     */
    private async flushRenderQueueAsync(): Promise<void> {
        if (this.isRendering) {
            return;
        }
        this.isRendering = true;
        try {
            while (this.pendingViewType !== null) {
                const currentViewType = this.pendingViewType;
                this.pendingViewType = null;
                await this.renderCurrentViewAsync(currentViewType);
            }
        } finally {
            this.isRendering = false;
        }
    }

    /**
     * 渲染当前标准视图到预览画布。
     *
     * @param viewType 标准视图类型。
     */
    private async renderCurrentViewAsync(viewType: StandardViewType): Promise<void> {
        const viewer = this.boundViewer;
        const canvas = this.canvasElement;
        if (!viewer || !canvas) {
            return;
        }

        const frame = viewer.buildStandardViewPreviewFrame(viewType, {
            fov: this.previewCamera.fov,
            aspect: this.previewCamera.aspect,
        });
        if (!frame) {
            return;
        }

        await this.renderFrameAsync(canvas, viewer.scene, frame, ViewOrientationPreviewRenderer.PREVIEW_SIZE);
    }

    /**
     * 将主场景以指定相机帧渲染到目标画布。
     *
     * @param canvas 目标 HTMLCanvasElement（正方形）。
     * @param scene 主场景引用。
     * @param frame 标准视图相机帧（位置 / 目标 / 朝上向量）。
     * @param size 正方形画布的像素边长。
     */
    private async renderFrameAsync(
        canvas: HTMLCanvasElement,
        scene: THREE.Scene,
        frame: StandardViewCameraFrame,
        size: number,
    ): Promise<void> {
        if (this.currentCanvas !== canvas) {
            this.disposeRenderer();
        }

        if (!this.renderer) {
            await this.initRenderer(canvas, size);
        }

        const renderer = this.renderer;
        if (!renderer) {
            return;
        }

        const currentSize = renderer.getSize(new THREE.Vector2());
        if (currentSize.x !== size || currentSize.y !== size) {
            renderer.setSize(size, size, false);
        }

        this.previewCamera.position.set(frame.position[0], frame.position[1], frame.position[2]);
        this.previewCamera.up.set(frame.up[0], frame.up[1], frame.up[2]);
        this.previewCamera.lookAt(frame.target[0], frame.target[1], frame.target[2]);

        this.syncPreviewCameraClipping(frame);

        this.previewCamera.updateProjectionMatrix();

        // 预览相机已通过 layer 过滤配置排除视角定位 mesh，直接渲染
        renderer.render(scene, this.previewCamera);
    }


    /**
     * 根据当前预览帧同步预览相机裁剪面。
     *
     * 设计说明：
     * 1. 预览帧会针对可见模型包围盒独立 fit，毫米级模型的相机距离可能远小于默认 `near=0.1`；
     * 2. 若继续使用固定 near，模型会整体位于近裁剪面之前，表现为右上角预览空白；
     * 3. 裁剪面只作用于独立预览相机，不改变主视口相机和 CameraControls 状态。
     *
     * @param frame 当前预览相机帧。
     */
    private syncPreviewCameraClipping(frame: StandardViewCameraFrame): void {
        const position = new THREE.Vector3(...frame.position);
        const target = new THREE.Vector3(...frame.target);
        const distance = Math.max(position.distanceTo(target), Number.EPSILON);
        const near = Math.max(
            ViewOrientationPreviewRenderer.PREVIEW_CAMERA_MIN_NEAR,
            Math.min(
                ViewOrientationPreviewRenderer.PREVIEW_CAMERA_DEFAULT_NEAR,
                distance * ViewOrientationPreviewRenderer.PREVIEW_CAMERA_NEAR_DISTANCE_RATIO,
            ),
        );
        const far = Math.max(
            ViewOrientationPreviewRenderer.PREVIEW_CAMERA_MIN_FAR,
            distance * ViewOrientationPreviewRenderer.PREVIEW_CAMERA_FAR_DISTANCE_RATIO,
            near * 100,
        );

        this.previewCamera.near = near;
        this.previewCamera.far = far;
    }


    /**
     * 初始化 WebGPU 渲染器并绑定到画布。
     *
     * @param canvas 目标画布。
     * @param size 初始渲染尺寸。
     */
    private async initRenderer(canvas: HTMLCanvasElement, size: number): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = (async () => {
            const renderer = new THREE.WebGPURenderer({
                canvas,
                antialias: true,
                alpha: true,
            });
            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.setSize(size, size, false);
            await renderer.init();
            this.renderer = renderer;
            this.currentCanvas = canvas;

            // 独立预览 canvas 必须和主视口背景保持一致，否则暗色主题下会出现白色预览块。
            this.applyPreviewBackgroundColor();

        })();

        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    /**
     * 挂载预览浮层到 Viewer 容器右上角。
     *
     * @param container Viewer 视口容器。
     */
    private ensureOverlayMounted(container: HTMLElement): void {
        if (!this.overlayElement || !this.canvasElement) {
            const overlay = document.createElement('div');
            overlay.style.position = 'absolute';
            overlay.style.top = '16px';
            overlay.style.right = '16px';
            overlay.style.width = `${ViewOrientationPreviewRenderer.PREVIEW_SIZE}px`;
            overlay.style.height = `${ViewOrientationPreviewRenderer.PREVIEW_SIZE}px`;
            overlay.style.borderRadius = '8px';
            overlay.style.border = '1px solid rgba(100, 100, 255, 0.4)';

            overlay.style.background = this.previewBackgroundColor;

            overlay.style.pointerEvents = 'none';
            overlay.style.zIndex = '200';
            overlay.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.5)';
            overlay.style.overflow = 'hidden';

            const canvas = document.createElement('canvas');
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.display = 'block';

            canvas.style.background = this.previewBackgroundColor;

            overlay.appendChild(canvas);

            this.overlayElement = overlay;
            this.canvasElement = canvas;
        }

        const hostStyle = window.getComputedStyle(container);
        if (hostStyle.position === 'static') {
            container.style.position = 'relative';
        }

        if (this.overlayElement.parentElement !== container) {
            this.overlayElement.parentElement?.removeChild(this.overlayElement);
            container.appendChild(this.overlayElement);
        }

        this.applyPreviewBackgroundColor();

    }


    /**
     * 从宿主容器同步初始主题背景。
     *
     * 设计说明：
     * 1. 主题事件不是 ReplaySubject，预览渲染器可能晚于首次主题派发创建；
     * 2. 主视口 `render-container` 已持有最终背景，因此从容器样式读取当前状态最可靠；
     * 3. 读取失败时默认亮色背景，避免出现透明浮层显示不确定底色。
     *
     * @param container Viewer 视口容器。
     */
    private syncInitialThemeBackground(container: HTMLElement): void {
        const resolvedBackground = window.getComputedStyle(container).backgroundColor;
        this.previewBackgroundColor = this.resolveThemeBackgroundColor(resolvedBackground);
        this.applyPreviewBackgroundColor();
    }

    /**
     * 订阅主题变化事件。
     */
    private subscribeThemeChange(): void {
        this.themeSubscription?.unsubscribe();
        this.themeSubscription = this.eventBus.add(
            ThemeEventName.ThemeChanged,
            this.handleThemeChangedEvent,
        );
    }

    /**
     * 根据主题变化事件同步预览背景。
     *
     * @param payload 主题变化事件载荷。
     */
    private readonly handleThemeChanged = (
        payload: ThemeChangedEventPayload,
    ): void => {
        this.previewBackgroundColor = payload.viewportBackgroundColor;
        this.applyPreviewBackgroundColor();
    };

    /**
     * 处理事件总线的弱类型主题载荷。
     *
     * @param payload 主题变化事件载荷。
     */
    private readonly handleThemeChangedEvent = (
        payload: unknown,
    ): void => {
        this.handleThemeChanged(payload as ThemeChangedEventPayload);
    };

    /**
     * 根据 CSS 背景色解析当前主题背景枚举。
     *
     * @param backgroundColor CSS 背景色。
     * @returns 主题视口背景色枚举。
     */
    private resolveThemeBackgroundColor(backgroundColor: string): CadThemeViewportBackgroundColor {
        const normalizedBackground = backgroundColor.replace(/\s+/g, '').toLowerCase();

        // 浏览器通常把十六进制颜色解析为 `rgb(...)`，这里显式兼容深灰主题色和历史纯黑值。
        if (
            normalizedBackground === CadThemeViewportBackgroundColor.Dark
            || normalizedBackground === 'rgb(51,51,51)'
            || normalizedBackground === 'rgba(51,51,51,1)'
            || normalizedBackground === '#000000'
            || normalizedBackground === 'black'
            || normalizedBackground === 'rgb(0,0,0)'
            || normalizedBackground === 'rgba(0,0,0,1)'
        ) {
            return CadThemeViewportBackgroundColor.Dark;
        }

        // 除暗色主题背景外统一回退纯白，保证预览背景只使用主题协议允许的有限选项。
        return CadThemeViewportBackgroundColor.Light;
    }

    /**
     * 应用当前视图定向预览背景。
     */
    private applyPreviewBackgroundColor(): void {
        if (this.overlayElement) {
            // 外层浮层需要和 canvas 同步，避免透明边缘露出旧背景。
            this.overlayElement.style.background = this.previewBackgroundColor;
        }

        if (this.canvasElement) {
            // canvas 自身设置背景，覆盖 WebGPU 透明输出下的底色。
            this.canvasElement.style.background = this.previewBackgroundColor;
        }

        if (this.renderer) {
            // renderer.domElement 与 canvasElement 指向同一元素；保留显式写入便于阅读和排查。
            this.renderer.domElement.style.background = this.previewBackgroundColor;
        }
    }


    /**
     * 设置预览浮层显示状态。
     *
     * @param visible 是否显示。
     */
    private setOverlayVisible(visible: boolean): void {
        if (!this.overlayElement) {
            return;
        }
        this.overlayElement.style.display = visible ? 'block' : 'none';
    }

    /**
     * 销毁当前渲染器并清空绑定引用。
     */
    private disposeRenderer(): void {
        this.renderer?.dispose();
        this.renderer = null;
        this.currentCanvas = null;
        this.initPromise = null;
    }
}
